import React, {useCallback, useContext} from "react";
import {EditorContext, useEditorState} from "@tiptap/react";
import {Menu, MenuButton, MenuItem, MenuItems, Transition} from "@headlessui/react";
import {FaChevronDown} from "react-icons/fa";
import {TextSelection} from "@tiptap/pm/state";
import {canSplit} from "@tiptap/pm/transform";
import HeadingIcon from "../icons/HeadingIcon";
import HeadingOneIcon from "../icons/HeadingOneIcon";
import HeadingTwoIcon from "../icons/HeadingTwoIcon";
import HeadingThreeIcon from "../icons/HeadingThreeIcon";
import HeadingFourIcon from "../icons/HeadingFourIcon";
import HeadingFiveIcon from "../icons/HeadingFiveIcon";
import HeadingSixIcon from "../icons/HeadingSixIcon";

const headingIcons = {
    1: HeadingOneIcon,
    2: HeadingTwoIcon,
    3: HeadingThreeIcon,
    4: HeadingFourIcon,
    5: HeadingFiveIcon,
    6: HeadingSixIcon,
};

const toggleHeading = (editor, level) => {
    if (!editor || editor.isDestroyed) return;

    const shouldSetParagraph = editor.isActive("heading", {level});

    editor.chain()
        .focus()
        .command(({tr}) => {
            if (tr.selection.empty) return true;

            let {from, to} = tr.selection;

            const splitSelectionBoundary = (position, association) => {
                const resolvedPosition = tr.doc.resolve(position);

                if (
                    !resolvedPosition.parent.isTextblock ||
                    resolvedPosition.parentOffset === 0 ||
                    resolvedPosition.parentOffset === resolvedPosition.parent.content.size ||
                    !canSplit(tr.doc, position)
                ) {
                    return {from, to};
                }

                const mappingStart = tr.mapping.maps.length;
                tr.split(position);
                const splitMapping = tr.mapping.slice(mappingStart);

                return {
                    from: splitMapping.map(from, association === 1 ? 1 : -1),
                    to: splitMapping.map(to, association),
                };
            };

            ({from, to} = splitSelectionBoundary(to, -1));
            ({from, to} = splitSelectionBoundary(from, 1));

            tr.setSelection(TextSelection.create(tr.doc, from, to));
            return true;
        })
        [shouldSetParagraph ? "setParagraph" : "setHeading"](
            shouldSetParagraph ? undefined : {level}
        )
        .run();
};

const HeadingButton = ({editor, level}) => {
    const Icon = headingIcons[level];
    const isActive = useEditorState({
        editor,
        selector: ({editor}) => !!editor?.isActive("heading", {level}),
    });

    const handleClick = useCallback((event) => {
        event.preventDefault();
        toggleHeading(editor, level);
    }, [editor, level]);

    if (!Icon) return null;

    return (
        <MenuItem>
            <button
                type="button"
                className={"rich-text-button" + (isActive ? " active" : "")}
                onMouseDown={(event) => event.preventDefault()}
                onClick={handleClick}
            >
                <Icon className="rich-text-button-icon"/> Título {level}
            </button>
        </MenuItem>
    );
};

const RichTextHeadingDropdownMenuContent = ({editor, levels}) => {
    const activeLevel = useEditorState({
        editor,
        selector: ({editor}) => {
            if (!editor) return null;
            return levels.find((level) => editor.isActive("heading", {level})) ?? null;
        },
    });

    const ActiveIcon = activeLevel ? headingIcons[activeLevel] : HeadingIcon;

    return (
        <Menu>
            <MenuButton type="button" className="rich-text-button">
                <ActiveIcon className="rich-text-button-icon"/>
                <FaChevronDown className="rich-text-button-dropdown-small"/>
            </MenuButton>
            <Transition
                enter="transition ease-out duration-75"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
            >
                <MenuItems anchor="bottom" className="heading-dropdown">
                    {levels.map((level) => (
                        <HeadingButton editor={editor} level={level} key={level}/>
                    ))}
                </MenuItems>
            </Transition>
        </Menu>
    );
};
 
const RichTextHeadingDropdownMenu = ({levels}) => {
    const {editor} = useContext(EditorContext);

    if (!editor || !editor.isEditable) return null;

    return <RichTextHeadingDropdownMenuContent editor={editor} levels={levels}/>;
};

export default RichTextHeadingDropdownMenu;
